import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { pickupRegions } from './view/data';

export default function GroupUpdateModal({ open, onClose, bucket, onSave, loading }) {
  const [form, setForm] = useState({
    pickup_area: '', 
    pickup_price: '',
    promotion: '',
    deliveryExtraMethod: '',
  });

  const isPaid = bucket?.group?.payed === true || bucket?.group?.pay_status === 'PAID';

  useEffect(() => {
    const grp = bucket?.group || {};
    setForm({
      pickup_area: grp.pickup_area ?? '',
      pickup_price: grp.pickup_price ?? '',
      promotion: grp.promotion ?? '',
      deliveryExtraMethod: grp.deliveryExtraMethod ?? '',
    });
  }, [bucket]);

  const handleChange = (key) => (e) => {
    let value;
    if (key === 'pickup_price' || key === 'promotion') {
      const v = e.target.value;
      value = v === '' ? '' : Number(v);
    } else {
      // eslint-disable-next-line
      value = e.target.value;
    }
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleSubmit = () => {
    const dataToSend = isPaid
      ? {
          // If paid, only allow deliveryExtraMethod to be updated
          deliveryExtraMethod: form.deliveryExtraMethod === '' ? null : form.deliveryExtraMethod,
        }
      : {
          // If not paid, allow all fields to be updated
          ...form,
          // convert back for API
          pickup_area: form.pickup_area === '' ? null : form.pickup_area,
          pickup_price:
            form.pickup_price === '' || Number.isNaN(Number(form.pickup_price))
              ? null
              : Number(form.pickup_price),
          promotion:
            form.promotion === '' || Number.isNaN(Number(form.promotion))
              ? null
              : Number(form.promotion),
          deliveryExtraMethod: form.deliveryExtraMethod === '' ? null : form.deliveryExtraMethod,
        };

    onSave?.({
      groupId: bucket?.groupId,
      data: dataToSend,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Group</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Pick-up Area (Select with clear X) */}
          <FormControl fullWidth variant="outlined" disabled={isPaid}>
            <InputLabel id="pickup-area-edit-label">Pick-up Area</InputLabel>
            <Select
              labelId="pickup-area-edit-label"
              label="Pick-up Area"
              value={form.pickup_area ?? ''} // string
              onChange={handleChange('pickup_area')}
              disabled={isPaid}
              input={
                <OutlinedInput
                  endAdornment={
                    form.pickup_area && !isPaid ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear selection"
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm((s) => ({ ...s, pickup_area: '' })); // clear to empty string
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                />
              }
            >
              {pickupRegions?.map((r) => (
                <MenuItem key={r.id} value={String(r.id)}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Pick-up Price"
            value={form.pickup_price}
            type="number"
            inputProps={{ step: '0.01' }}
            onChange={handleChange('pickup_price')}
            disabled={isPaid}
            fullWidth
          />

          <TextField
            label="Promotion"
            value={form.promotion}
            type="number"
            onChange={handleChange('promotion')}
            disabled={isPaid}
            fullWidth
          />

          {/* Delivery Extra Method */}
          <FormControl fullWidth variant="outlined">
            <InputLabel id="delivery-extra-method-label">Delivery Extra Method</InputLabel>
            <Select
              labelId="delivery-extra-method-label"
              label="Delivery Extra Method"
              value={form.deliveryExtraMethod ?? ''}
              onChange={handleChange('deliveryExtraMethod')}
              input={
                <OutlinedInput
                  endAdornment={
                    form.deliveryExtraMethod ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear selection"
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm((s) => ({ ...s, deliveryExtraMethod: '' }));
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }
                />
              }
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="TBILISI">Tbilisi</MenuItem>
              <MenuItem value="REGION">Region</MenuItem>
              <MenuItem value="PICKUP">Pickup</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

GroupUpdateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bucket: PropTypes.shape({
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    group: PropTypes.shape({
      pickup_area: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pickup_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      promotion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      deliveryExtraMethod: PropTypes.string,
      payed: PropTypes.bool,
      pay_status: PropTypes.string,
    }),
  }),
  onSave: PropTypes.func,
  loading: PropTypes.bool,
};

GroupUpdateModal.defaultProps = {
  bucket: null,
  onSave: undefined,
  loading: false,
};
